import React, {useMemo} from 'react';
import {DndProvider} from 'react-dnd';
import {Tree, NodeModel, MultiBackend, getBackendOptions, DropOptions} from '@minoru/react-dnd-treeview';
import {CiselnikVO} from '../../../api';
import {buildCiselnikTreeOrder} from '../../../helpers/Formatter';
import {CiselnikTreeNode} from './CiselnikTreeNode';
import {Placeholder} from '../../treeActions/Placeholder';
import {CustomDragPreview} from '../../treeActions/CustomDragPreview';

type Props = {
    /** Plochý seznam hodnot číselníku (řídící pole formuláře). */
    ciselnik: CiselnikVO[];
    /** Přesun hodnoty pod jiného rodiče (změna úrovně) – vrací nové pole hodnot. Mění jen parentTempId, API zůstává beze změny. */
    onChange: (ciselnik: CiselnikVO[]) => void;
    onEdit: (ciselnik: CiselnikVO) => void;
    onRemove: (ciselnik: CiselnikVO) => void;
};

/**
 * Strom hodnot číselníku s přesouváním pomocí drag&drop (po vzoru treeActions/TreeActions).
 * Hodnoty jsou v rámci každé úrovně řazeny abecedně; drag&drop mění pouze vazbu
 * {@link CiselnikVO.parentTempId} (úroveň) – API zůstává beze změny.
 */
export const CiselnikTree: React.FC<Props> = ({ciselnik, onChange, onEdit, onRemove}) => {
    const knownTempIds = useMemo(() => new Set(ciselnik.map(c => c.tempId).filter(Boolean)), [ciselnik]);

    // Uzly v pořadí odsazeného stromu (v rámci úrovně abecedně, jako ve zbytku aplikace); knihovna z vazeb parent sestaví hierarchii.
    const treeData: NodeModel<CiselnikVO>[] = useMemo(
        () =>
            buildCiselnikTreeOrder(ciselnik).map(({ciselnik: c}) => ({
                id: c.tempId as string,
                parent: c.parentTempId && knownTempIds.has(c.parentTempId) ? c.parentTempId : 0,
                text: c.nazev ?? '',
                droppable: true,
                data: c,
            })),
        [ciselnik, knownTempIds],
    );

    const allNodeIds = useMemo(() => treeData.map(node => node.id), [treeData]);

    /** Je `targetId` potomkem `sourceId` (nebo přímo jím samým)? Chrání proti vytvoření cyklu. */
    const isSelfOrDescendant = (sourceId: string, targetId: string | number): boolean => {
        let current: string | number | undefined = targetId;
        while (current !== undefined && current !== 0) {
            if (current === sourceId) {
                return true;
            }
            current = treeData.find(node => node.id === current)?.parent;
        }
        return false;
    };

    const handleDrop = (_newTree: NodeModel<CiselnikVO>[], {dragSource, dropTargetId}: DropOptions<CiselnikVO>) => {
        if (!dragSource) {
            return;
        }
        const draggedTempId = String(dragSource.id);
        const newParentTempId = dropTargetId === 0 ? undefined : String(dropTargetId);

        const updated = ciselnik.map(c => (c.tempId === draggedTempId ? {...c, parentTempId: newParentTempId} : c));
        onChange(updated);
    };

    return (
        <DndProvider backend={MultiBackend} options={getBackendOptions()}>
            <div className={'tree-wrapper'}>
                <Tree
                    tree={treeData}
                    initialOpen={allNodeIds}
                    rootId={0}
                    render={(node, {onToggle}) => (
                        <CiselnikTreeNode node={node} onToggle={onToggle} onEdit={onEdit} onRemove={onRemove} />
                    )}
                    dragPreviewRender={monitorProps => <CustomDragPreview monitorProps={monitorProps} />}
                    onDrop={handleDrop}
                    classes={{
                        root: 'treeRoot',
                        draggingSource: 'draggingSource',
                        placeholder: 'placeholderContainer',
                    }}
                    sort={false}
                    insertDroppableFirst={false}
                    canDrop={(_tree, {dragSource, dropTargetId}) => {
                        if (!dragSource) {
                            return false;
                        }
                        // Nelze přesunout hodnotu pod sebe sama ani pod svého potomka (prevence cyklu).
                        return !isSelfOrDescendant(String(dragSource.id), dropTargetId);
                    }}
                    dropTargetOffset={8}
                    placeholderRender={(node, {depth}) => <Placeholder node={node} depth={depth} />}
                />
            </div>
        </DndProvider>
    );
};
